import type { ViewResponse } from "@redotech/effect-api-shared/rpc/view-folder";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import type { FilterGroup } from "@redotech/redo-model/views/views";
import { encodeAdvancedFilterGroup } from "@redotech/redo-web/advanced-filters/advanced-filter";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { catchApiErrors } from "../db/error-handling";
import {
  buildAssignedViewFilterGroup,
  DefaultView,
  DRAFTS_VIEW_FILTER_GROUP,
  OPEN_VIEW_FILTER_GROUP,
} from "../views/default-views";

export const conversationCountsQueryKey = ["conversationCounts"] as const;

function encodeFilterGroup(fg: FilterGroup | null): string | null {
  if (!fg) return null;
  try {
    return encodeAdvancedFilterGroup(fg);
  } catch {
    return null;
  }
}

export function useConversationCounts(
  views: ViewResponse[],
): Map<string, number> {
  const user = useRequiredContext(UserContext);
  const userId = user._id.toString();
  const effectApi = useRequiredContext(EffectApiClientContext);

  const viewsForCounts = useMemo(() => {
    const result: { viewKey: string; filterGroup: string | null }[] = [
      {
        viewKey: DefaultView.OPEN,
        filterGroup: encodeFilterGroup(OPEN_VIEW_FILTER_GROUP),
      },
      {
        viewKey: DefaultView.DRAFTS,
        filterGroup: encodeFilterGroup(DRAFTS_VIEW_FILTER_GROUP),
      },
    ];

    if (userId) {
      result.push({
        viewKey: DefaultView.ASSIGNED,
        filterGroup: encodeFilterGroup(buildAssignedViewFilterGroup(userId)),
      });
    }

    for (const view of views) {
      result.push({
        viewKey: String(view.id),
        filterGroup: encodeFilterGroup(
          (view.filterGroup as FilterGroup | null) ?? null,
        ),
      });
    }

    return result;
  }, [views, userId]);

  const { data } = useQuery({
    queryKey: [
      ...conversationCountsQueryKey,
      viewsForCounts.map((v) => v.viewKey),
    ],
    queryFn: async () => {
      const client = effectApi.getClient();
      const result = await effectApi.runPromise(
        catchApiErrors(
          client.conversationCounts.getConversationCounts({
            payload: { views: viewsForCounts },
          }),
        ),
      );
      return result as Record<string, number>;
    },
    enabled: viewsForCounts.length > 0,
    staleTime: 0,
  });

  return useMemo(() => {
    const map = new Map<string, number>();
    if (data) {
      for (const [key, count] of Object.entries(data)) {
        map.set(key, count);
      }
    }
    return map;
  }, [data]);
}
